import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { WasteStatus } from "../lib/use-waste-status.hook";
import {
  BrandIcWasteField,
  IcCancel,
  IcCar,
  IcExpand,
  IcGarrage,
  IcLocation,
  IcMinimize,
  IcHighPriority,
} from "@racoon/icons";
import type { TruckProviderGroup } from "../lib/use-trucks-by-provider.hook";
import { Namespace, KeysOf, useTypedT } from "@racoon/i18n";
import { fmt } from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { fromTimestamp, enumColors } from "@racoon/lib";
import { useTruckByIdQuery } from "~/shared/api/govDash/govDash.api";
import { getJobPhotos, getPickupLocationStatus, getPickupStatusColor } from "~/shared/utils";
import { formatTimestampToDateRu } from "~/shared/utils/filterDateFormatter";
import { JobPhotoReport } from "~/ui/components/jobPhotoReport";
import type { EnrichedPickupLocation } from "./map.dashboard";

const readDashboardMapState = () => {
  const raw = sessionStorage.getItem("dashboard_map_state");
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

// ── Default Bottom Sheet: Waste collection status ──
export function BottomSheetDefault({
  wasteStatus,
  truckProviderGroups = [],
  pickupLocations = [],
}: {
  wasteStatus: WasteStatus;
  truckProviderGroups?: TruckProviderGroup[];
  // The same pickup locations rendered on the map. Deriving the progress bar from
  // their per-location job status (via getPickupLocationStatus) keeps it in sync
  // with the map pins/clusters, including the "not completed" (red) bucket.
  pickupLocations?: EnrichedPickupLocation[];
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isProvidersExpanded, setIsProvidersExpanded] = useState(false);
  const t = useTypedT(Namespace.Govdash);

  const totalTrucks = truckProviderGroups.reduce((acc, group) => acc + group.truckCount, 0);

  // Bucket each location the same way the map pins/clusters do.
  const locationStats = useMemo(() => {
    let visited = 0;
    let waiting = 0;
    let notCompleted = 0;
    let unknown = 0;
    for (const loc of pickupLocations) {
      switch (getPickupLocationStatus(loc)) {
        case ConnectProto.JobStatus.COMPLETED:
        case ConnectProto.JobStatus.FORCE_COMPLETED:
          visited++;
          break;
        case ConnectProto.JobStatus.CREATED:
        case ConnectProto.JobStatus.ASSIGNED:
        case ConnectProto.JobStatus.ENROUTE:
        case ConnectProto.JobStatus.ON_SERVICE:
          waiting++;
          break;
        case ConnectProto.JobStatus.NOT_COMPLETED:
          notCompleted++;
          break;
        default:
          unknown++;
      }
    }
    const plan = pickupLocations.length;
    const pct = (val: number) => (plan > 0 ? Number(((val / plan) * 100).toFixed(1)) : 0);
    return {
      plan,
      visited,
      waiting,
      notCompleted,
      unknown,
      visitedPercent: pct(visited),
      waitingPercent: pct(waiting),
      notCompletedPercent: pct(notCompleted),
      unknownPercent: pct(unknown),
    };
  }, [pickupLocations]);

  return (
    <div className="lg:rounded-b-2xl w-full bg-white lg:border border-t border-slate-200 rounded-t-2xl shadow-[0_4px_20px_-10px_rgba(0,0,0,0.07)] max-h-[50vh] flex flex-col overflow-hidden">
      <div className="flex flex-col overflow-y-auto">
        {/* Sticky Header Section */}
        <div className="sticky top-0 bg-white z-20 flex flex-col gap-2 pt-2">
          <div className="flex items-center justify-between px-4">
            <div className="flex gap-2 items-center">
              <p className="font-semibold text-base leading-4 tracking-tight text-text-primary">
                {t("status.wasteCollectionStatus")}
              </p>
              <p className="text-sm font-normal leading-[14px] tracking-tight text-text-secondary">
                {wasteStatus.date}
              </p>
            </div>
            <button
              type="button"
              aria-label={isExpanded ? t("actions.shrinkStatus") : t("actions.expandStatus")}
              className="w-8 h-8 flex items-center justify-center shrink-0 border-none bg-transparent p-0 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? (
                <IcMinimize className="w-4 h-4 text-brand-blue" />
              ) : (
                <IcExpand className="w-4 h-4 text-brand-blue" />
              )}
            </button>
          </div>
          <div className="w-full h-px bg-slate-200" />
        </div>

        {/* Scrollable Content Section */}
        <div className="flex lg:flex-row flex-col gap-4 py-4 flex-1">
          {/* Trucks by provider — Always visible after trucks bar */}
          {truckProviderGroups.length > 0 && (
            <div className="flex flex-col gap-4 flex-1">
              {/* Color bar showing provider distribution */}
              <div className="px-4 flex gap-2 items-center">
                <div className="flex flex-col items-center min-w-10 gap-1.5 shrink-0">
                  <IcCar className="w-6 h-6" />
                  <span className="text-sm font-medium text-text-primary">{totalTrucks}</span>
                </div>
                <div className="flex gap-0 items-stretch w-full h-6 rounded-lg overflow-hidden">
                  {truckProviderGroups.map((group) => (
                    <div
                      key={group.providerId}
                      className="flex items-center justify-center py-1 transition-[flex] duration-500"
                      style={{
                        backgroundColor: group.color,
                        flex: group.truckCount,
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Provider list — visible when expanded */}
              {isExpanded && (
                <div className="flex flex-col gap-2 px-4">
                  {truckProviderGroups
                    .slice(0, isProvidersExpanded ? undefined : 4)
                    .map((group, index) => (
                      <div key={group.providerId} className="flex items-center justify-between">
                        <div className="flex gap-2 items-center flex-1 min-w-0">
                          <span className="font-medium text-sm leading-[14px] tracking-tight text-text-primary shrink-0 w-5 text-center">
                            {index + 1}
                          </span>
                          <div
                            className="w-3 h-3 rounded-full shrink-0"
                            style={{ backgroundColor: group.color }}
                          />
                          <p className="font-medium text-sm leading-[14px] tracking-tight text-text-primary truncate">
                            {group.providerName}
                          </p>
                        </div>
                        <p className="font-medium text-sm leading-[14px] tracking-tight text-text-primary whitespace-nowrap ml-4">
                          {t("status.trucks_count", { count: group.truckCount })}
                        </p>
                      </div>
                    ))}
                  {truckProviderGroups.length > 4 && (
                    <div className="w-full flex justify-center">
                      <button
                        type="button"
                        className="text-brand-blue text-sm font-medium self-start mt-1 cursor-pointer hover:opacity-80 transition-opacity p-0 border-none bg-transparent"
                        onClick={() => setIsProvidersExpanded(!isProvidersExpanded)}
                      >
                        {isProvidersExpanded ? t("actions.showLess") : t("actions.showMore")}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {isExpanded && <div className="lg:hidden w-full h-px bg-slate-200" />}

          <div className="flex flex-col gap-4 flex-1">
            {/* Locations Progress bar */}
            <div className="px-4 flex flex-col gap-4">
              {isExpanded && (
                <div className="lg:hidden w-full flex justify-between">
                  <div className="text-sm font-medium text-text-primary">
                    {t("status.wasteCollection")}
                  </div>
                </div>
              )}
              <div className="flex gap-2 items-center w-full">
                <div className="flex flex-col items-center min-w-10 gap-1.5 shrink-0">
                  <IcLocation className="w-6 h-6 text-text-primary" />
                  <span className="text-sm font-medium text-text-primary">
                    {locationStats.plan}
                  </span>
                </div>
                <div className="flex flex-1 h-6 rounded-lg overflow-hidden">
                  <div
                    className="bg-[#34c759] flex items-center justify-center py-1 transition-[flex] duration-500"
                    style={{ flex: locationStats.visited }}
                  />
                  <div
                    className="bg-brand-blue flex items-center justify-center py-1 transition-[flex] duration-500"
                    style={{ flex: locationStats.waiting }}
                  />
                  <div
                    className="bg-[#FF3B30] flex items-center justify-center py-1 transition-[flex] duration-500"
                    style={{ flex: locationStats.notCompleted }}
                  />
                </div>
              </div>
            </div>

            {/* Expanded details — Locations */}
            {isExpanded && (
              <div
                className="grid gap-y-2 gap-x-12 px-4"
                style={{ gridTemplateColumns: "1fr auto auto" }}
              >
                {/* Planned */}
                <p className="font-medium text-sm leading-[14px] tracking-tight text-text-primary">
                  {t("status.planned")}
                </p>
                <p className="font-medium text-sm leading-[14px] tracking-tight text-text-primary text-right">
                  {t("status.points_count", { count: locationStats.plan })}
                </p>
                <p className="font-normal text-sm leading-[14px] tracking-tight text-text-primary text-right">
                  100%
                </p>

                {/* Exported */}
                <div className="flex ml-2 gap-1 items-center">
                  <div className="w-3 h-3 rounded-full bg-[#34c759] shrink-0" />
                  <p className="font-normal text-sm leading-[14px] tracking-tight text-text-primary">
                    {t("status.exported")}
                  </p>
                </div>
                <p className="font-normal text-sm leading-[14px] tracking-tight text-text-primary text-right">
                  {t("status.points_count", { count: locationStats.visited })}
                </p>
                <p className="font-normal text-sm leading-[14px] tracking-tight text-text-primary text-right">
                  {locationStats.visitedPercent}%
                </p>

                {/* Waiting */}
                <div className="flex ml-2 gap-1 items-center">
                  <div className="w-3 h-3 rounded-full bg-brand-blue shrink-0" />
                  <p className="font-normal text-sm leading-[14px] tracking-tight text-text-primary">
                    {t("status.waiting")}
                  </p>
                </div>
                <p className="font-normal text-sm leading-[14px] tracking-tight text-text-primary text-right">
                  {t("status.points_count", { count: locationStats.waiting })}
                </p>
                <p className="font-normal text-sm leading-[14px] tracking-tight text-text-primary text-right">
                  {locationStats.waitingPercent}%
                </p>

                {/* Not completed */}
                <div className="flex ml-2 gap-1 items-center">
                  <div className="w-3 h-3 rounded-full bg-[#FF3B30] shrink-0" />
                  <p className="font-normal text-sm leading-[14px] tracking-tight text-text-primary">
                    {t("analytics.status.notExported")}
                  </p>
                </div>
                <p className="font-normal text-sm leading-[14px] tracking-tight text-text-primary text-right">
                  {t("status.points_count", { count: locationStats.notCompleted })}
                </p>
                <p className="font-normal text-sm leading-[14px] tracking-tight text-text-primary text-right">
                  {locationStats.notCompletedPercent}%
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Truck Bottom Sheet ──
export function BottomSheetTruck({
  truckId,
  govGeoAccesses,
  onClose,
}: {
  truckId: string | number;
  govGeoAccesses: ConnectProto.GovGeoAccess[];
  onClose: () => void;
}) {
  const t = useTypedT(Namespace.Govdash);
  const navigate = useNavigate();
  const trimmedTruckId = typeof truckId === "string" ? truckId.trim() : null;
  const hasValidTruckId =
    typeof truckId === "string"
      ? /^\d+$/.test(trimmedTruckId || "") && BigInt(trimmedTruckId || "0") > BigInt(0)
      : Number.isSafeInteger(truckId as number) && (truckId as number) > 0;
  const truckQueryId = hasValidTruckId
    ? typeof truckId === "string"
      ? BigInt(trimmedTruckId || "0")
      : BigInt(truckId as number)
    : BigInt(0);
  const { data: truckData } = useTruckByIdQuery({ id: truckQueryId }, { skip: !hasValidTruckId });
  const truck = truckData?.truck?.truck;

  // Find district name by looking through govGeoAccesses for the service provider
  // Each GovGeoAccess contains a list of serviceProviders for that district
  const districtName = (() => {
    const serviceProviderId = truckData?.truck?.serviceProvider?.id;
    if (!serviceProviderId) return "-";
    const spId = serviceProviderId.toString();
    for (const geoAccess of govGeoAccesses) {
      const sps = geoAccess.serviceProviders || [];
      if (sps.some((sp) => (sp.id?.toString() || sp.id) === spId)) {
        return geoAccess.areaName || "-";
      }
    }
    return "-";
  })();

  const handleViewDetails = () => {
    const mapState = sessionStorage.getItem("dashboard_map_state");
    const state = {
      from: "dashboard",
      mapState: mapState ? JSON.parse(mapState) : null,
      showTrucks: true,
    };
    navigate(`/records/trucks/${truckId}`, { state });
  };

  return (
    <div className="w-full lg:max-w-[360px] lg:h-full lg:max-h-full max-h-[50vh] bg-white lg:border border-t border-slate-200 lg:rounded-2xl rounded-t-2xl shadow-[0_4px_20px_-10px_rgba(0,0,0,0.07)] flex flex-col overflow-hidden">
      <div className="flex flex-col gap-4 py-4 overflow-y-auto flex-1 min-h-0">
        {/* Header */}
        <div className="flex gap-2 items-start justify-between px-4">
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            {/* Truck icon + plate */}
            <div className="flex gap-2 items-center">
              <IcCar className="w-6 h-6 text-brand-blue flex-shrink-0" />
              <span className="border border-text-primary rounded p-1 px-2 font-semibold text-base leading-4 tracking-tight text-text-primary">
                {truck?.plateNumber || "N/A"}
              </span>
            </div>
            {/* Company + type */}
            <div className="flex gap-2 items-center text-sm font-medium leading-[14px] tracking-tight">
              <span className="text-text-primary">
                {truck?.ownerOrganizationId?.toString() || "N/A"}
              </span>
              <span className="text-text-secondary">
                {truck?.truckType?.model || truck?.model || "N/A"}
              </span>
            </div>
          </div>
          <button
            type="button"
            aria-label={t("actions.close")}
            className="w-6 h-6  flex items-center justify-center cursor-pointer shrink-0 border-none p-0"
            onClick={onClose}
          >
            <IcCancel className="w-6 h-6 " />
          </button>
        </div>

        {/* Separator */}
        <div className="w-full h-px bg-slate-200" />

        {/* Info rows */}
        <div className="flex flex-col gap-4 px-4">
          {/* Online */}
          {/* <div className="flex items-start justify-between w-full text-sm leading-4 tracking-tight whitespace-nowrap">
            <span className="font-medium text-text-secondary">{t("status.onLine")}</span>
            <div className="flex gap-2 items-center">
              <span className="font-normal text-text-primary">{"-"}</span>
            </div>
          </div> */}

          {/* Daily GPS mileage */}
          {/* <div className="flex items-start justify-between w-full text-sm leading-4 tracking-tight whitespace-nowrap">
            <span className="font-medium text-text-secondary">{t("status.dailyMileage")}</span>
            <span className="font-normal text-text-primary">{"-"}</span>
          </div> */}

          {/* Locations visited today */}
          {/* <div className="flex items-start justify-between w-full text-sm leading-4 tracking-tight whitespace-nowrap">
            <span className="font-medium text-text-secondary">
              {t("status.locationsVisitedToday")}
            </span>
            <span className="font-normal text-text-primary">{"-"}</span>
          </div>  */}

          {/* Responsible MSW operator */}
          <div className="flex items-start justify-between w-full text-sm leading-4 tracking-tight whitespace-nowrap">
            <span className="font-medium text-text-secondary">{t("status.responsibleMvo")}</span>
            <span className="font-normal text-text-primary">
              {truckData?.truck?.serviceProvider?.name}
            </span>
          </div>

          {/* District */}
          <div className="flex items-start justify-between w-full text-sm leading-4 tracking-tight whitespace-nowrap">
            <span className="font-medium text-text-secondary">{t("status.districtInfo")}</span>
            <span className="font-normal text-text-primary">{districtName}</span>
          </div>
        </div>
      </div>

      {/* Action button — pinned to the bottom of the sheet, outside the scroll area */}
      <div className="px-4 py-4 shrink-0 border-t border-slate-200 bg-white">
        <button
          type="button"
          onClick={handleViewDetails}
          className="w-full bg-[#007aff] hover:bg-[#0051cc] active:bg-[#003c99] text-white font-medium text-sm leading-4 tracking-tight py-3 px-4 rounded-lg transition-colors duration-200"
        >
          {t("actions.findInTruckRecords")}
        </button>
      </div>
    </div>
  );
}

// ── Pickup Location Bottom Sheet ──
export function BottomSheetPickupLocation({
  locationId,
  pickupLocation,
  onClose,
}: {
  locationId: number | string;
  pickupLocation: EnrichedPickupLocation;
  onClose: () => void;
}) {
  const t = useTypedT(Namespace.Govdash);
  const tEnum = useTypedT(Namespace.Enum);
  const navigate = useNavigate();

  const serviceProviderId = pickupLocation?._serviceProvider?.id;

  // Location pin color follows the pickup location's job status
  const statusColor = getPickupStatusColor(getPickupLocationStatus(pickupLocation));

  const handleMvoClick = () => {
    if (serviceProviderId) {
      const mapState = sessionStorage.getItem("dashboard_map_state");
      const state = {
        from: "dashboard",
        mapState: mapState ? JSON.parse(mapState) : null,
      };
      navigate(`/records/service-providers/${serviceProviderId}`, { state });
    }
  };

  const totalVolume =
    pickupLocation?.containerGroups?.reduce(
      (sum, group) => sum + (group.numberOfContainers || 0) * (group.containerType?.volume || 0),
      0,
    ) || 0;

  if (!pickupLocation) {
    return null;
  }

  return (
    <div className="w-full lg:max-w-[360px] lg:h-full lg:max-h-full max-h-[50vh] bg-white lg:border border-t border-slate-200 lg:rounded-2xl rounded-t-2xl shadow-[0_4px_20px_-10px_rgba(0,0,0,0.07)] flex flex-col overflow-hidden">
      <div className="flex flex-col gap-4 py-4 overflow-y-auto flex-1 min-h-0">
        {/* Header */}
        <div className="flex gap-2 items-start justify-between px-4">
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            {/* Icon + name */}
            <div className="flex gap-1 items-center">
              <IcLocation className="w-6 h-6 flex-shrink-0" style={{ color: statusColor }} />
              <p className="font-semibold text-base leading-4 tracking-tight text-text-primary">
                {pickupLocation.name || "N/A"}
              </p>
            </div>
            {/* Type */}
            <p className="text-sm font-medium leading-[14px] tracking-tight text-text-primary">
              {t("status.commonPickupLocation")}
            </p>
          </div>
          <button
            type="button"
            aria-label={t("actions.close")}
            className="w-6 h-6 rounded-full flex items-center justify-center cursor-pointer shrink-0 border-none p-0 "
            onClick={onClose}
          >
            <IcCancel className="w-6 h-6 " />
          </button>
        </div>

        {/* Separator */}
        <div className="w-full h-px bg-slate-200" />

        <div className="flex flex-col gap-4 px-4">
          <div
            className={`flex items-start justify-between w-full text-sm leading-4 tracking-tight whitespace-nowrap`}
            onKeyDown={
              serviceProviderId
                ? (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleMvoClick();
                    }
                  }
                : undefined
            }
            role={serviceProviderId ? "button" : undefined}
            tabIndex={serviceProviderId ? 0 : -1}
            aria-disabled={!serviceProviderId}
          >
            <span className="font-medium text-text-secondary">{t("status.responsibleMvo")}</span>
            <span
              onClick={serviceProviderId ? handleMvoClick : undefined}
              className={`font-normal text-brand-blue ${serviceProviderId ? "cursor-pointer hover:underline" : "cursor-default"}`}
            >
              {pickupLocation?._serviceProvider?.name}
            </span>
          </div>
          {/* Total nominal volume */}
          <div className="flex items-start justify-between w-full text-sm leading-4 tracking-tight whitespace-nowrap">
            <span className="font-medium text-text-secondary">
              {t("status.totalNominalVolume")}
            </span>
            <span className="font-normal text-text-primary">
              {t("status.volumeMetric", {
                volume: totalVolume.toFixed(2) ?? "-",
              })}
            </span>
          </div>
        </div>

        {/* Jobs */}
        {pickupLocation._jobs?.length > 0 &&
          pickupLocation._jobs.map((job, index) => {
            const jobInstant = fromTimestamp(job.factEndAt ?? job.updatedAt);
            const statusKey =
              `JOB_STATUS_${ConnectProto.JobStatus[job.status ?? ConnectProto.JobStatus.UNDEFINED]}` as KeysOf<Namespace.Enum>;
            const statusColor = enumColors[statusKey];

            const { beforePhotos, afterPhotos, failureReasonPhotos } = getJobPhotos(job);

            return (
              <div key={job.id ?? index} className="flex flex-col gap-4">
                {/* Separator */}
                <div className="w-full border-t" />

                {/* Job */}
                <div className="flex flex-col gap-4 px-4">
                  <div className="flex items-start justify-between w-full text-sm leading-4 tracking-tight whitespace-nowrap">
                    <Link
                      to={`/records/waste-export/${job.id}`}
                      state={{
                        from: "dashboard",
                        mapState: readDashboardMapState(),
                      }}
                      className="text-base font-medium text-brand-blue cursor-pointer hover:underline"
                    >
                      {t("wasteExport.task")} {job.id}
                    </Link>
                    <div className="flex items-center gap-2">
                      <span>{jobInstant ? fmt.time(jobInstant) : "-"}</span>
                      <div
                        className={`min-w-[110px] py-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs justify-center ${statusColor?.bg ?? ""} ${statusColor?.color ?? ""} ${statusColor?.borderColor ? `${statusColor.borderColor} border` : ""}`}
                      >
                        {tEnum(statusKey)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Info rows */}
                <div className="flex flex-col gap-4 px-4">
                  {/* Container groups list */}
                  <div className="flex flex-col gap-4 w-full">
                    <div className="flex flex-col gap-4 w-full text-sm leading-4 tracking-tight">
                      <div className="flex items-center justify-between w-full whitespace-nowrap">
                        <p className="font-medium text-text-secondary">{t("containerGroup")} 1</p>
                        <div className="flex gap-1 items-center font-normal text-text-primary">
                          <span>{job.containerType?.name || t("unknownType")}</span>
                          <span>|</span>
                          <span>
                            {t("status.count", {
                              count: job.containerGroup?.numberOfContainers || 0,
                            })}
                          </span>
                        </div>
                      </div>
                      {job.containerGroup?.ownerOrganization?.name && (
                        <div className="flex items-center justify-between w-full whitespace-nowrap">
                          <span className="font-medium text-text-secondary whitespace-nowrap">
                            {t("status.wasteGenerator")}
                          </span>
                          <span className="font-normal text-text-primary text-right">
                            {job.containerGroup.ownerOrganization.name}
                          </span>
                        </div>
                      )}

                      {job.status === ConnectProto.JobStatus.NOT_COMPLETED && (
                        <div className="flex items-center justify-between w-full whitespace-nowrap">
                          <span className="font-medium text-text-secondary whitespace-nowrap">
                            {t("wasteExport.reason")}
                          </span>
                          <span className="font-normal text-brand-red text-right">
                            {job?.driverComment?.split("=")[1]?.split(":")[0]}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-4 space-y-4">
                  <p className="text-base font-semibold tracking-tight text-slate-900">
                    {t("wasteExport.photoReport")}
                  </p>
                  {beforePhotos.length === 0 &&
                  afterPhotos.length === 0 &&
                  failureReasonPhotos.length === 0 ? (
                    <p className="text-sm text-text-secondary">{t("wasteExport.noPhotos")}</p>
                  ) : (
                    <JobPhotoReport
                      className="space-y-4"
                      beforePhotos={beforePhotos}
                      afterPhotos={afterPhotos}
                      failureReasonPhotos={failureReasonPhotos}
                    />
                  )}
                </div>
              </div>
            );
          })}
      </div>

      {/* Action button — pinned to the bottom of the sheet, outside the scroll area */}
      <div className="px-4 py-4 shrink-0 border-t border-slate-200 bg-white">
        <button
          type="button"
          onClick={() => {
            const mapState = sessionStorage.getItem("dashboard_map_state");
            navigate(`/records/pickup-locations/${locationId}`, {
              state: {
                from: "dashboard",
                mapState: mapState ? JSON.parse(mapState) : null,
              },
            });
          }}
          className="w-full bg-[#007aff] hover:bg-[#0051cc] active:bg-[#003c99] text-white font-medium text-sm leading-4 tracking-tight py-3 px-4 rounded-lg transition-colors duration-200"
        >
          {t("actions.findInPickupLocationRecords")}
        </button>
      </div>
    </div>
  );
}

// ── Disposal Site Bottom Sheet ──
export function BottomSheetDisposalSite({
  site,
  onClose,
}: {
  site: ConnectProto.Location;
  onClose: () => void;
}) {
  const t = useTypedT(Namespace.Govdash);

  return (
    <div className="w-full lg:max-w-[360px] lg:h-full lg:max-h-full max-h-[50vh] bg-white lg:border border-t border-slate-200 lg:rounded-2xl rounded-t-2xl shadow-[0_4px_20px_-10px_rgba(0,0,0,0.07)] flex flex-col overflow-hidden">
      <div className="flex flex-col gap-4 py-4 overflow-y-auto flex-1 min-h-0">
        {/* Header */}
        <div className="flex gap-2 items-start justify-between px-4">
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            {/* Icon + name */}
            <div className="flex gap-1 items-center">
              <BrandIcWasteField className="w-6 h-6 flex-shrink-0" />
              <p className="font-semibold text-base leading-4 tracking-tight text-text-primary">
                {site.name || "N/A"}
              </p>
            </div>
            {/* Type */}
            <p className="text-sm font-medium leading-[14px] tracking-tight text-text-secondary">
              {t("subtitle.disposalSite")} {t("tabs.tbo")}
            </p>
          </div>
          <button
            type="button"
            aria-label={t("actions.close")}
            className="w-6 h-6 rounded-full flex items-center justify-center cursor-pointer shrink-0 border-none p-0"
            onClick={onClose}
          >
            <IcCancel className="w-6 h-6" />
          </button>
        </div>

        {/* Separator */}
        <div className="w-full h-px bg-slate-200" />

        {/* Info rows */}
        <div className="flex flex-col gap-4 px-4">
          {/* Address */}
          <div className="flex items-start justify-between w-full text-sm leading-4 tracking-tight">
            <span className="font-medium text-text-secondary whitespace-nowrap">
              {t("status.address")}
            </span>
            <span className="font-normal text-text-primary text-right">-</span>
          </div>

          {/* Coordinates */}
          <div className="flex items-start justify-between w-full text-sm leading-4 tracking-tight whitespace-nowrap">
            <span className="font-medium text-text-secondary">{t("status.coordinates")}</span>
            <span className="font-normal text-text-primary">
              {site.lat?.toFixed(6)}, {site.lon?.toFixed(6)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Depot Bottom Sheet ──
export function BottomSheetDepot({
  depot,
  onClose,
}: {
  depot: ConnectProto.GovDepotInfo;
  onClose: () => void;
}) {
  const t = useTypedT(Namespace.Govdash);
  const depotLocation = depot.depot;

  return (
    <div className="w-full lg:max-w-[360px] lg:h-full lg:max-h-full max-h-[50vh] bg-white lg:border border-t border-slate-200 lg:rounded-2xl rounded-t-2xl shadow-[0_4px_20px_-10px_rgba(0,0,0,0.07)] flex flex-col overflow-hidden">
      <div className="flex flex-col gap-4 py-4 overflow-y-auto flex-1 min-h-0">
        {/* Header */}
        <div className="flex gap-2 items-start justify-between px-4">
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            {/* Icon + name */}
            <div className="flex gap-1 items-center">
              <IcGarrage className="w-6 h-6 flex-shrink-0 text-[#DC2626]" />
              <p className="font-semibold text-base leading-4 tracking-tight text-text-primary">
                {depotLocation?.name || "N/A"}
              </p>
            </div>
            {/* Type + Owner */}
            <p className="text-sm font-medium leading-[14px] tracking-tight text-text-secondary">
              {t("subtitle.depot")} • {depot.ownerOrganization?.name || "-"}
            </p>
          </div>
          <button
            type="button"
            aria-label={t("actions.close")}
            className="w-6 h-6 rounded-full flex items-center justify-center cursor-pointer shrink-0 border-none p-0"
            onClick={onClose}
          >
            <IcCancel className="w-6 h-6" />
          </button>
        </div>

        {/* Separator */}
        <div className="w-full h-px bg-slate-200" />

        {/* Info rows */}
        <div className="flex flex-col gap-4 px-4">
          {/* Number of trucks */}
          <div className="flex items-start justify-between w-full text-sm leading-4 tracking-tight whitespace-nowrap">
            <span className="font-medium text-text-secondary">{t("status.numberOfTrucks")}</span>
            <span className="font-normal text-text-primary">
              {depot.numberOfTrucks?.toString() || "-"}
            </span>
          </div>

          {/* Coordinates */}
          <div className="flex items-start justify-between w-full text-sm leading-4 tracking-tight whitespace-nowrap">
            <span className="font-medium text-text-secondary">{t("status.coordinates")}</span>
            <span className="font-normal text-text-primary">
              {depotLocation?.lat?.toFixed(6)}, {depotLocation?.lon?.toFixed(6)}
            </span>
          </div>
          <div className="flex items-start justify-between w-full text-sm leading-4 tracking-tight flex-wrap gap-2">
            <span className="font-medium text-text-secondary">{t("status.address")}</span>
            <span className="font-normal text-text-primary">{depotLocation?.address || "-"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Suspicious Location Bottom Sheet ──
export function BottomSheetSuspiciousLocation({
  suspiciousLocation,
  onClose,
}: {
  suspiciousLocation: ConnectProto.SuspiciousLocation;
  onClose: () => void;
}) {
  const t = useTypedT(Namespace.Govdash);
  const navigate = useNavigate();

  const location = suspiciousLocation.location;
  const locationId = location?.id;
  const createdDate = location?.createdAt ? formatTimestampToDateRu(location.createdAt) : "N/A";

  const handleFindInRegistry = () => {
    // Navigate to suspicious locations registry or open details
    const mapState = sessionStorage.getItem("dashboard_map_state");
    navigate(locationId ? `/records/suspicious-sites/${locationId}` : "/records/suspicious-sites", {
      state: {
        from: "dashboard",
        mapState: mapState ? JSON.parse(mapState) : null,
      },
    });
  };

  return (
    <div className="w-full lg:max-w-[360px] lg:h-full lg:max-h-full max-h-[60vh] bg-white lg:border border-t border-slate-200 lg:rounded-2xl rounded-t-2xl shadow-[0_4px_20px_-10px_rgba(0,0,0,0.07)] flex flex-col overflow-hidden">
      <div className="flex flex-col overflow-y-auto flex-1 min-h-0">
        {/* Header */}
        <div className="flex items-start gap-2 px-4 pt-4 pb-3">
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            {/* Icon + name */}
            <div className="flex gap-2 items-center">
              <IcHighPriority className="w-6 h-6 flex-shrink-0 text-[#DC2626]" />
              <p className="font-semibold text-base leading-4 tracking-tight text-text-primary">
                {location?.name || "N/A"}
              </p>
            </div>
            {/* Subtitle */}
            <p className="text-sm font-medium leading-[14px] tracking-tight text-text-secondary">
              {t("subtitle.suspiciousSite")}
            </p>
          </div>
          <button
            type="button"
            aria-label={t("actions.close")}
            className="w-6 h-6 rounded-full flex items-center justify-center cursor-pointer shrink-0 border-none p-0"
            onClick={onClose}
          >
            <IcCancel className="w-6 h-6" />
          </button>
        </div>

        {/* Separator */}
        <div className="w-full h-px bg-slate-200" />

        {/* Info rows */}
        <div className="flex flex-col gap-4 px-4 py-4">
          {/* Created date */}
          <div className="flex items-start justify-between w-full text-sm leading-4 tracking-tight">
            <span className="font-medium text-text-secondary">{t("status.createdAt")}</span>
            <span className="font-normal text-text-primary">{createdDate}</span>
          </div>

          {/* Coordinates */}
          <div className="flex items-start justify-between w-full text-sm leading-4 tracking-tight">
            <span className="font-medium text-text-secondary">{t("status.coordinates")}</span>
            <span className="font-normal text-text-primary">
              {location?.lat?.toFixed(6)}, {location?.lon?.toFixed(6)}
            </span>
          </div>
        </div>
      </div>

      {/* Action button — pinned to the bottom of the sheet, outside the scroll area */}
      <div className="px-4 py-4 shrink-0 border-t border-slate-200 bg-white">
        <button
          type="button"
          onClick={handleFindInRegistry}
          className="w-full bg-[#007aff] hover:bg-[#0051cc] active:bg-[#003c99] text-white font-medium text-sm leading-4 tracking-tight py-3 px-4 rounded-lg transition-colors duration-200"
        >
          {t("actions.findInSuspiciousLocationRecords")}
        </button>
      </div>
    </div>
  );
}
